import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { LuLock, LuWallet } from "react-icons/lu";
import { getCashState } from "../../api/cash";
import { flushAllProductChanges } from "../../services/productSyncQueue";
import { Catalog } from "./catalog";
import "./cashSaleGate.css";

// Only the sale surface is gated. Header navigation and order history stay outside.
export const CashSaleGate = ({ children, onOpenCash }) => {
  const point = useSelector(s => s.aplicationConfig.corporationData.pointConfig?.PointOfSaleConfigurationId);
  const operator = useSelector(s => s.aplicationConfig.userConfig.data?.userId);
  const identity = `${point || ""}:${operator || ""}`;
  const [result, setResult] = useState({ identity: null, status: "loading" });
  const [reading, setReading] = useState(true);
  const [navigating, setNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState("");
  const refresh = useRef(() => {});
  const leaving = useRef(false);
  useEffect(() => {
    let live = true, inFlight = false;
    setResult({ identity, status: "loading" });
    const read = async () => {
      if (inFlight) return;
      inFlight = true;
      setReading(true);
      try {
        if (!point || !operator) throw Error("Missing selection");
        const response = await getCashState();
        const data = response?.data;
        if (response?.error !== false || data?.pointOfSaleId !== point || data?.operatorId !== operator)
          throw Error("Unconfirmed cash state");
        if (data.current !== null && (!data.current?.shiftId || data.current.status !== "OPEN"))
          throw Error("Unconfirmed shift");
        if (live) setResult({ identity, status: data.current ? "open" : "closed" });
      } catch {
        if (live) setResult({ identity, status: "error" });
      } finally {
        inFlight = false;
        if (live) setReading(false);
      }
    };
    const visible = () => { if (document.visibilityState === "visible") void read(); };
    refresh.current = read;
    void read();
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", visible);
    const timer = window.setInterval(read, 15000);
    return () => {
      live = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [point, operator, identity]);
  const status = result.identity === identity ? result.status : "loading";
  const open = async () => {
    if (leaving.current) return;
    leaving.current = true;
    setNavigating(true);
    setNavigationError("");
    try {
      if (!(await flushAllProductChanges())) throw Error("Pending changes");
      onOpenCash();
    } catch {
      setNavigationError("Hay cambios sin guardar. Comprueba la conexión y vuelve a intentarlo.");
    } finally {
      leaving.current = false;
      setNavigating(false);
    }
  };
  return (
    <section className="tpvSaleGate" aria-label="Zona de venta">
      {status === "open" ? children : (
        <>
          <fieldset className="tpvSaleLocked" disabled inert="" aria-hidden="true">
            <div className="commander-body">
              <Catalog />
              <div className="commander-order tpvSaleLockedOrder">
                <strong>Comanda</strong>
                <span>La comanda se conserva mientras la caja está cerrada.</span>
              </div>
            </div>
          </fieldset>
          <div className="tpvSaleLockOverlay">
            <div className="tpvSaleLockNotice">
              <LuLock aria-hidden="true" />
              <h1>{status === "loading" ? "Comprobando caja…" : status === "error" ? "No se pudo comprobar la caja" : "Caja cerrada"}</h1>
              <p>{status === "error" ? "Reintenta la lectura antes de empezar una venta." : "Abre la caja de este TPV para seleccionar productos y vender."} Puedes consultar los pedidos en Historial.</p>
              {navigationError && <p role="alert">{navigationError}</p>}
              {status === "closed" && <button type="button" onClick={open} disabled={navigating}><LuWallet aria-hidden="true" />{navigating ? "Abriendo formulario…" : "Abrir caja"}</button>}
              {status === "error" && <button type="button" onClick={() => refresh.current()} disabled={reading}>{reading ? "Comprobando…" : "Reintentar"}</button>}
              {status === "loading" && <span role="status">Consultando el estado de caja de este puesto.</span>}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
